import { ElementType, useRef } from 'react';
import { useDigitalFormsFreemiumContext } from '@frontend/digital-forms-scope';
import { useTranslation } from '@frontend/i18n';
import { Icon, IconName } from '@frontend/icons';
import { theme } from '@frontend/theme';
import { NakedButton, PolymorphicComponentPropWithoutRef, Text, useTooltip, Button } from '@frontend/design-system';
import { tooltipArrowStyle, upgradeButtonStyle, nakedButtonStyle, textStyle } from './form-packet-list-item.style';

type FormPacketListItemProps = {
  name: string;
  iconName: IconName;
  isSelected: boolean;
  onSelect: () => void;
  isLocked?: boolean;
};

type PolymorphicProps<C extends ElementType> = PolymorphicComponentPropWithoutRef<C, FormPacketListItemProps>;

export const FormPacketListItem = <C extends ElementType = 'li'>({
  as,
  name,
  iconName,
  isSelected,
  onSelect,
  isLocked,
  ...rest
}: PolymorphicProps<C>) => {
  const Component = as || 'li';
  const ref = useRef<HTMLButtonElement>(null);

  const { t } = useTranslation('form-packet-selector');
  const { isActionBlocked, featureUpgradeDetailsModalControls } = useDigitalFormsFreemiumContext();
  const shouldDisableForm = isActionBlocked('form_listing_view') && isLocked;
  const { tooltipProps, triggerProps, Tooltip } = useTooltip({
    placement: 'bottom',
  });

  const onClick = () => {
    if (shouldDisableForm) return;
    if (ref.current) {
      ref.current.blur();
    }
    onSelect();
  };

  return (
    <Component {...rest}>
      <div {...triggerProps}>
        <NakedButton ref={ref} onClick={onClick} css={nakedButtonStyle(isSelected)} disabled={shouldDisableForm}>
          <Icon
            name={iconName}
            css={{
              color: shouldDisableForm ? theme.colors.neutral10 : theme.colors.neutral70,
            }}
          />
          <Text weight='bold' color={shouldDisableForm ? 'disabled' : 'default'} css={textStyle} title={name}>
            {name}
          </Text>
        </NakedButton>
        {shouldDisableForm && (
          <Tooltip {...tooltipProps} css={tooltipArrowStyle}>
            {t('To use this form, upgrade your Forms Add-on.')}
            <br />
            <Button
              variant='tertiary'
              css={upgradeButtonStyle}
              onClick={() => {
                featureUpgradeDetailsModalControls.openModal();
              }}
            >
              {t('Upgrade Now')}
            </Button>
          </Tooltip>
        )}
      </div>
    </Component>
  );
};
